import { Inject, Injectable, PLATFORM_ID } from "@angular/core";
import { argbFromHex, themeFromSourceColor, CustomColor, hexFromArgb } from '../../libs/material-color-utilities';
import { ColorVariables } from "./theme.service.config";
import { Theming } from "../../state-management/model/interfaces/state.model";
import { FontFamily } from "../../state-management/model/enums/state.enums";
import { isPlatformBrowser } from "@angular/common";
import { ɵSharedStylesHost as SharedStylesHost } from '@angular/platform-browser';

@Injectable({ providedIn: "root" })
export class ThemeService {

    private typographyVariantsBrand = ['display', 'headline'];
    private typographyVariantsPlain = ['body', 'label', 'title'];
    private typographySizes = ['small', 'medium', 'large'];

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private sharedStylesHost: SharedStylesHost
    ) { }

    updateTheming(theming: Theming) {
        if (typeof theming.colors.primaryColor === "string") {
            const colorTheme = this.#generateColorTheme(theming.colors.primaryColor, []);
            this.#setColorTheme(colorTheme);
        }
        const typographieTheme = this.#generateTypographieTheme(theming.typography.brandTypography, theming.typography.plainTypography);
        this.#setTypographieTheme(typographieTheme);
        this.#setLightMode(theming.lightMode);
    }

    #generateTypographieTheme(brandFontFamily: FontFamily, plainFontFamily: FontFamily): { [key: string]: string } {
        const fontDictionary: Record<string, string> = {};
        const addFontToDictionary = (variant: string, size: string, fontFamily: string) => {
            fontDictionary[`${variant}-${size}-font`] = `${fontFamily}`;
        };
        // Brand fonts
        this.typographyVariantsBrand.forEach((variant) => {
            this.typographySizes.forEach((size) => {
                addFontToDictionary(variant, size, brandFontFamily);
            });
        });
        // Plain fonts
        this.typographyVariantsPlain.forEach((variant) => {
            this.typographySizes.forEach((size) => {
                addFontToDictionary(variant, size, plainFontFamily);
            });
        });
        return fontDictionary;
    }


    #generateColorTheme(primaryColor: string, customColors: CustomColor[]): { [key: string]: { light: string, dark: string } } {
        const colorTheme = themeFromSourceColor(argbFromHex(primaryColor), customColors);

        const get_primary_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.primary.getHct(number).toInt());
        };
        const get_secondary_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.secondary.getHct(number).toInt());
        };
        const get_tertiary_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.tertiary.getHct(number).toInt());
        };
        const get_neutral_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.neutral.getHct(number).toInt());
        };
        const get_neutralVariant_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.neutralVariant.getHct(number).toInt());
        };
        const get_error_color = (number: number): string => {
            return hexFromArgb(colorTheme.palettes.error.getHct(number).toInt());
        };

        return {
            'background': {
                light: get_neutral_color(ColorVariables.BACKGROUND_LIGHT),
                dark: get_neutral_color(ColorVariables.BACKGROUND_DARK)
            },
            'error': {
                light: get_error_color(ColorVariables.ERROR_LIGHT),
                dark: get_error_color(ColorVariables.ERROR_DARK)
            },
            'error-container': {
                light: get_error_color(ColorVariables.ERROR_CONTAINER_LIGHT),
                dark: get_error_color(ColorVariables.ERROR_CONTAINER_DARK)
            },
            'inverse-on-surface': {
                light: get_neutral_color(ColorVariables.INVERSE_ON_SURFACE_LIGHT),
                dark: get_neutral_color(ColorVariables.INVERSE_ON_SURFACE_DARK)
            },
            'inverse-primary': {
                light: get_primary_color(ColorVariables.INVERSE_PRIMARY_LIGHT),
                dark: get_primary_color(ColorVariables.INVERSE_PRIMARY_DARK)
            },
            'inverse-surface': {
                light: get_neutral_color(ColorVariables.INVERSE_SURFACE_LIGHT),
                dark: get_neutral_color(ColorVariables.INVERSE_SURFACE_DARK)
            },
            'on-background': {
                light: get_neutral_color(ColorVariables.ON_BACKGROUND_LIGHT),
                dark: get_neutral_color(ColorVariables.ON_BACKGROUND_DARK)
            },
            'on-error': {
                light: get_error_color(ColorVariables.ON_ERROR_LIGHT),
                dark: get_error_color(ColorVariables.ON_ERROR_DARK)
            },
            'on-error-container': {
                light: get_error_color(ColorVariables.ON_ERROR_CONTAINER_LIGHT),
                dark: get_error_color(ColorVariables.ON_ERROR_CONTAINER_DARK)
            },
            'on-primary': {
                light: get_primary_color(ColorVariables.ON_PRIMARY_LIGHT),
                dark: get_primary_color(ColorVariables.ON_PRIMARY_DARK)
            },
            'on-primary-container': {
                light: get_primary_color(ColorVariables.ON_PRIMARY_CONTAINER_LIGHT),
                dark: get_primary_color(ColorVariables.ON_PRIMARY_CONTAINER_DARK)
            },
            'on-primary-fixed': {
                light: get_primary_color(ColorVariables.ON_PRIMARY_FIXED),
                dark: get_primary_color(ColorVariables.ON_PRIMARY_FIXED)
            },
            'on-primary-fixed-variant': {
                light: get_primary_color(ColorVariables.ON_PRIMARY_FIXED_VARIANT),
                dark: get_primary_color(ColorVariables.ON_PRIMARY_FIXED_VARIANT)
            },
            'on-secondary': {
                light: get_secondary_color(ColorVariables.ON_SECONDARY_LIGHT),
                dark: get_secondary_color(ColorVariables.ON_SECONDARY_DARK)
            },
            'on-secondary-container': {
                light: get_secondary_color(ColorVariables.ON_SECONDARY_CONTAINER_LIGHT),
                dark: get_secondary_color(ColorVariables.ON_SECONDARY_CONTAINER_DARK)
            },
            'on-secondary-fixed': {
                light: get_secondary_color(ColorVariables.ON_SECONDARY_FIXED),
                dark: get_secondary_color(ColorVariables.ON_SECONDARY_FIXED)
            },
            'on-secondary-fixed-variant': {
                light: get_secondary_color(ColorVariables.ON_SECONDARY_FIXED_VARIANT),
                dark: get_secondary_color(ColorVariables.ON_SECONDARY_FIXED_VARIANT)
            },
            'on-surface': {
                light: get_neutral_color(ColorVariables.ON_SURFACE_LIGHT),
                dark: get_neutral_color(ColorVariables.ON_SURFACE_DARK)
            },
            'on-surface-variant': {
                light: get_neutralVariant_color(ColorVariables.ON_SURFACE_VARIANT_LIGHT),
                dark: get_neutralVariant_color(ColorVariables.ON_SURFACE_VARIANT_DARK)
            },
            'on-tertiary': {
                light: get_tertiary_color(ColorVariables.ON_TERTIARY_LIGHT),
                dark: get_tertiary_color(ColorVariables.ON_TERTIARY_DARK)
            },
            'on-tertiary-container': {
                light: get_tertiary_color(ColorVariables.ON_TERTIARY_CONTAINER_LIGHT),
                dark: get_tertiary_color(ColorVariables.ON_TERTIARY_CONTAINER_DARK)
            },
            'on-tertiary-fixed': {
                light: get_tertiary_color(ColorVariables.ON_TERTIARY_FIXED),
                dark: get_tertiary_color(ColorVariables.ON_TERTIARY_FIXED)
            },
            'on-tertiary-fixed-variant': {
                light: get_tertiary_color(ColorVariables.ON_TERTIARY_FIXED_VARIANT),
                dark: get_tertiary_color(ColorVariables.ON_TERTIARY_FIXED_VARIANT)
            },
            'outline': {
                light: get_neutralVariant_color(ColorVariables.OUTLINE_LIGHT),
                dark: get_neutralVariant_color(ColorVariables.OUTLINE_DARK)
            },
            'outline-variant': {
                light: get_neutralVariant_color(ColorVariables.OUTLINE_VARIANT_LIGHT),
                dark: get_neutralVariant_color(ColorVariables.OUTLINE_VARIANT_DARK)
            },
            'primary': {
                light: get_primary_color(ColorVariables.PRIMARY_LIGHT),
                dark: get_primary_color(ColorVariables.PRIMARY_DARK)
            },
            'primary-container': {
                light: get_primary_color(ColorVariables.PRIMARY_CONTAINER_LIGHT),
                dark: get_primary_color(ColorVariables.PRIMARY_CONTAINER_DARK)
            },
            'primary-fixed': {
                light: get_primary_color(ColorVariables.PRIMARY_FIXED),
                dark: get_primary_color(ColorVariables.PRIMARY_FIXED)
            },
            'primary-fixed-dim': {
                light: get_primary_color(ColorVariables.PRIMARY_FIXED_DIM),
                dark: get_primary_color(ColorVariables.PRIMARY_FIXED_DIM)
            },
            'scrim': {
                light: get_neutral_color(ColorVariables.SCRIM_LIGHT),
                dark: get_neutral_color(ColorVariables.SCRIM_DARK)
            },
            'secondary': {
                light: get_secondary_color(ColorVariables.SECONDARY_LIGHT),
                dark: get_secondary_color(ColorVariables.SECONDARY_DARK)
            },
            'secondary-container': {
                light: get_secondary_color(ColorVariables.SECONDARY_CONTAINER_LIGHT),
                dark: get_secondary_color(ColorVariables.SECONDARY_CONTAINER_DARK)
            },
            'secondary-fixed': {
                light: get_secondary_color(ColorVariables.SECONDARY_FIXED),
                dark: get_secondary_color(ColorVariables.SECONDARY_FIXED)
            },
            'secondary-fixed-dim': {
                light: get_secondary_color(ColorVariables.SECONDARY_FIXED_DIM),
                dark: get_secondary_color(ColorVariables.SECONDARY_FIXED_DIM)
            },
            'shadow': {
                light: get_neutral_color(ColorVariables.SHADOW_LIGHT),
                dark: get_neutral_color(ColorVariables.SHADOW_DARK)
            },
            'surface': {
                light: get_neutral_color(ColorVariables.SURFACE_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_DARK)
            },
            'surface-bright': {
                light: get_neutral_color(ColorVariables.SURFACE_BRIGHT_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_BRIGHT_DARK)
            },
            'surface-container': {
                light: get_neutral_color(ColorVariables.SURFACE_CONTAINER_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_CONTAINER_DARK)
            },
            'surface-container-high': {
                light: get_neutral_color(ColorVariables.SURFACE_CONTAINER_HIGH_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_CONTAINER_HIGH_DARK)
            },
            'surface-container-highest': {
                light: get_neutral_color(ColorVariables.SURFACE_CONTAINER_HIGHEST_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_CONTAINER_HIGHEST_DARK)
            },
            'surface-container-low': {
                light: get_neutral_color(ColorVariables.SURFACE_CONTAINER_LOW_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_CONTAINER_LOW_DARK)
            },
            'surface-container-lowest': {
                light: get_neutral_color(ColorVariables.SURFACE_CONTAINER_LOWEST_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_CONTAINER_LOWEST_DARK)
            },
            'surface-dim': {
                light: get_neutral_color(ColorVariables.SURFACE_DIM_LIGHT),
                dark: get_neutral_color(ColorVariables.SURFACE_DIM_DARK)
            },
            'surface-tint': {
                light: get_primary_color(ColorVariables.SURFACE_DIM_LIGHT),
                dark: get_primary_color(ColorVariables.SURFACE_DIM_DARK)
            },
            'surface-variant': {
                light: get_neutralVariant_color(ColorVariables.SURFACE_VARIANT_LIGHT),
                dark: get_neutralVariant_color(ColorVariables.SURFACE_VARIANT_DARK)
            },
            'tertiary': {
                light: get_tertiary_color(ColorVariables.TERTIARY_LIGHT),
                dark: get_tertiary_color(ColorVariables.TERTIARY_DARK)
            },
            'tertiary-container': {
                light: get_tertiary_color(ColorVariables.TERTIARY_CONTAINER_LIGHT),
                dark: get_tertiary_color(ColorVariables.TERTIARY_CONTAINER_DARK)
            },
            'tertiary-fixed': {
                light: get_tertiary_color(ColorVariables.TERTIARY_FIXED),
                dark: get_tertiary_color(ColorVariables.TERTIARY_FIXED)
            },
            'tertiary-fixed-dim': {
                light: get_tertiary_color(ColorVariables.TERTIARY_FIXED_DIM),
                dark: get_tertiary_color(ColorVariables.TERTIARY_FIXED_DIM)
            }
        };
    }

    #setColorTheme(theme: { [key: string]: { light: string, dark: string } }): void {
        const prefix = '--mat-sys-';
        for (const key in theme) {
            if (theme.hasOwnProperty(key)) {
                // Hier wird das Tupel (light, dark) in ein Format überführt
                const { light, dark } = theme[key];
                this.#setThemeVariable(`${prefix}${key}`, `light-dark(${light}, ${dark})`);
            }
        }
    }

    #setTypographieTheme(theme: { [key: string]: string }): void {
        const prefix = '--custom-';
        for (const key in theme) {
            if (theme.hasOwnProperty(key)) {
                this.#setThemeVariable(`${prefix}${key}`, theme[key]);
            }
        }
    }

    #setLightMode(lightMode: boolean) {
        this.#setThemeVariable(`--COLOR-SCHEME`, lightMode ? 'only light' : 'only dark');
    }

    #setThemeVariable(variable: string, value: string): void {
        if (!isPlatformBrowser(this.platformId) || typeof document === 'undefined') {
            this.setServerCss(variable, value);
        } else {
            document.documentElement.style.setProperty(variable, value);
        }
    }

    private serverCssVariables: { [key: string]: string } = {};

    setServerCss(variable: string, value: string) {
        this.serverCssVariables = {}
        this.serverCssVariables[variable] = value;
        const cssVariables = Object.entries(this.serverCssVariables)
            .map(([key, val]) => `${key}: ${val};`)
            .join(' ');
        this.sharedStylesHost.addStyles([`:root { ${cssVariables} }`]);
    }
}